import React, { Fragment, useState, useEffect } from 'react';
import { useHistory, Link, useParams } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import { orderCancelAction, clearErrorAction } from '../../redux/actions/orderAction';
import Navbar from '../HomeScreen/Navbar';
import Footer from '../HomeScreen/bottomsection/Footer';
import Loader from '../../layout/loader';
import swal from 'sweetalert';
import { ORDER_CANCEL_REQUEST_RESET } from '../../redux/constants/orderConstants';
import MetaTag from '../../layout/MetaTag';

import CancelOutlinedIcon from '@mui/icons-material/CancelOutlined';
import ArrowBackIosNewOutlinedIcon from '@mui/icons-material/ArrowBackIosNewOutlined';
import VerifiedUserOutlinedIcon from '@mui/icons-material/VerifiedUserOutlined';

import './cancelOrderDetail.css';

function OrderCancelPage(props) {
  const history = useHistory();
  const dispatch = useDispatch();
  const routeParams = useParams();

  const id = routeParams?.id || props?.match?.params?.id;

  // Redux state
  const { user } = useSelector((state) => state.user);
  const { loading, isCancelled, error } = useSelector((state) => state.orderCancel);

  const [textAreaValue, setTextAreaValue] = useState('');
  const [cancelReasonPreset, setCancelReasonPreset] = useState('Order created by mistake');
  const [isChacked, setIsChacked] = useState(false);
  const [accept, setAccept] = useState(false);

  const cancelOrderHandler = (e) => {
    e.preventDefault();

    if (!isChacked) {
      return swal({
        title: 'Confirmation Needed',
        text: 'Please confirm that you really want to cancel this order.',
        icon: 'info',
      });
    }

    if (!accept) {
      return swal({
        title: 'Terms & Policy',
        text: 'Please accept the IM Online Cancellation and Refund policy to proceed.',
        icon: 'info',
      });
    }

    const fullReason = `${cancelReasonPreset}: ${textAreaValue.trim()}`;

    dispatch(
      orderCancelAction(
        user?.firstName || 'Customer',
        user?.email || '',
        user?.number || '',
        id,
        fullReason
      )
    );
  };

  useEffect(() => {
    if (error) {
      swal({
        title: 'Error',
        text: error,
        icon: 'error',
      });
      dispatch(clearErrorAction());
    }

    if (isCancelled) {
      swal({
        title: 'Order Cancellation Submitted',
        text: typeof isCancelled === 'string' ? isCancelled : 'Your cancellation request has been recorded.',
        icon: 'success',
      });
      history.push('/profile/Cancel-Orders-Detail');
      dispatch({ type: ORDER_CANCEL_REQUEST_RESET });
    }
  }, [dispatch, error, history, isCancelled]);

  return (
    <Fragment>
      <MetaTag
        title={`Cancel Order Request | IM Online Shopping`}
        description="Submit a cancellation request for your order on IM Online Shopping."
      />

      <div className="can-page-wrapper">
        <Navbar />

        <main className="can-container">
          {/* Breadcrumb */}
          <nav className="can-breadcrumb" aria-label="Breadcrumb">
            <Link to="/">Home</Link>
            <span className="can-breadcrumb-sep">/</span>
            <Link to="/userprofile">My Account</Link>
            <span className="can-breadcrumb-sep">/</span>
            <Link to="/profile/Cancel-Orders-Detail">Cancelled Orders</Link>
            <span className="can-breadcrumb-sep">/</span>
            <span className="can-breadcrumb-current">Cancel Request</span>
          </nav>

          {/* Hero Header */}
          <section className="can-hero">
            <div className="can-hero-left">
              <div className="can-hero-icon-box">
                <CancelOutlinedIcon />
              </div>
              <div className="can-hero-text">
                <h1>Order Cancellation Request</h1>
                <p>
                  Submit a cancellation request for Order{' '}
                  <strong style={{ color: '#111315' }}>#{String(id).slice(-8).toUpperCase()}</strong>.
                </p>
                <div className="can-policy-chips">
                  <span className="can-chip">
                    <VerifiedUserOutlinedIcon /> 100% Refund Guarantee
                  </span>
                  <span className="can-chip">Direct Payout to Bank / Source</span>
                </div>
              </div>
            </div>

            <div className="can-hero-actions">
              <Link to="/orders/Details/me" className="can-btn-back">
                <ArrowBackIosNewOutlinedIcon style={{ fontSize: '12px' }} />
                <span>Back to Orders</span>
              </Link>
            </div>
          </section>

          {loading ? (
            <Loader />
          ) : (
            <div className="can-form-card">
              <form onSubmit={cancelOrderHandler}>
                {/* Step 1: Agreements */}
                <div className="can-form-group">
                  <span className="can-form-label">Step 1: Cancellation Confirmation</span>

                  <label className="can-checkbox-row">
                    <input
                      type="checkbox"
                      checked={isChacked}
                      onChange={(e) => setIsChacked(e.target.checked)}
                      required
                    />
                    <span className="can-checkbox-text">
                      I confirm that I really want to cancel Order #{String(id).slice(-8).toUpperCase()}
                    </span>
                  </label>

                  <label className="can-checkbox-row">
                    <input
                      type="checkbox"
                      checked={accept}
                      onChange={(e) => setAccept(e.target.checked)}
                      required
                    />
                    <span className="can-checkbox-text">
                      I agree to IM Online Shopping's{' '}
                      <Link to="/about/return-and-exchange" target="_blank">
                        Cancellation & Refund Policy
                      </Link>
                      . Any prepaid amount will be refunded according to bank clearance timelines.
                    </span>
                  </label>
                </div>

                {/* Step 2: Reason Preset */}
                <div className="can-form-group">
                  <label htmlFor="can-preset-select" className="can-form-label">
                    Step 2: Select Primary Reason
                  </label>
                  <select
                    id="can-preset-select"
                    className="can-select-field"
                    value={cancelReasonPreset}
                    onChange={(e) => setCancelReasonPreset(e.target.value)}
                  >
                    <option value="Order created by mistake">Order created by mistake</option>
                    <option value="Found a better price elsewhere">Found a better price elsewhere</option>
                    <option value="Need to change delivery address or phone number">
                      Need to change delivery address or phone number
                    </option>
                    <option value="Need to change product size / color">
                      Need to change product size / color
                    </option>
                    <option value="Delivery time is too long">Delivery time is too long</option>
                    <option value="Decided not to purchase">Decided not to purchase</option>
                    <option value="Other reason">Other reason</option>
                  </select>
                </div>

                {/* Step 3: Detailed Comments */}
                <div className="can-form-group">
                  <label htmlFor="can-reason-text" className="can-form-label">
                    Step 3: Additional Notes / Explanation
                  </label>
                  <textarea
                    id="can-reason-text"
                    className="can-textarea-field"
                    placeholder="Provide any additional details or feedback for this cancellation..."
                    value={textAreaValue}
                    onChange={(e) => setTextAreaValue(e.target.value)}
                    required
                  />
                </div>

                {/* Form Actions */}
                <div className="can-form-actions">
                  <button type="submit" className="can-submit-btn" disabled={loading}>
                    <CancelOutlinedIcon fontSize="small" />
                    <span>{loading ? 'Submitting Cancellation...' : 'Confirm Order Cancellation'}</span>
                  </button>

                  <Link to="/orders/Details/me" className="can-btn-back" style={{ textAlign: 'center' }}>
                    <span>Keep My Order</span>
                  </Link>
                </div>
              </form>
            </div>
          )}
        </main>

        <Footer />
      </div>
    </Fragment>
  );
}

export default OrderCancelPage;